import {ILoesungshaeufigkeitenWerte, IPortalseite, ISchueler_testsAPI, ITestheftAPI} from "../Interfaces";
import Utilities from "./../Utilities";
import {IErgebnisseDB} from "../InterfacesDB";
import Rueckmeldung from "./Rueckmeldung";
import Tooltip from "../Tooltip";
import type TomSelectControl from 'tom-select';

declare const TomSelect: typeof TomSelectControl;

interface IFlussItem {
    item_id: number;
    testheft_id: number;
    domaene: string;
    aufgabe_bezeichnung: string;
    aufgabe_bezeichnung_kurz: string;
    richtig: number;
    iqb_lh: number;
}

interface IFlussVergleich {
    id: string;
    bezeichnung: string;
    werte: ReadonlyMap<string, number>;
    fehlenderVergleichsgrund: string | null;
}

export default class RueckmeldungFluss extends Rueckmeldung implements IPortalseite {
    private static readonly MINDESTANZAHL_BEARBEITUNGEN_PRO_ITEM = 3;
    private vergleichsgruppeId = '-';
    private vergleichsgruppeSelect: TomSelectControl | null = null;
    private chartBereinigungen: (() => void)[] = [];

    override async onSeiteLoad(): Promise<boolean> {
        await super.onSeiteLoad();
        await this.filterAnwenden();
        this.refreshDruckkopf();

        //Spaltenköpfe einbinden und Sortierung initialisieren
        if (this.domaenenInGruppe.length === 0)
            return true;

        // initiale Sortierung anzeigen
        document.addEventListener('refresh-rueckmeldung', () => {
            this.refreshLhFlussansicht();
        });
        document.dispatchEvent(new Event('refresh-rueckmeldung'));


        return true
    }

    override async onSeiteFinished(): Promise<boolean> {
        return true;
    }

    override async onSeiteLeave(): Promise<boolean> {
        this.bereinigeCharts();
        this.vergleichsgruppeSelect?.destroy();
        this.vergleichsgruppeSelect = null;
        return super.onSeiteLeave();
    }

    private refreshLhFlussansicht(): void {
        this.bereinigeCharts();
        this.refreshDruckkopf();

        //region Ergebnissarray nach Item aggregieren (wie in LH Aufgabenebene)
        const testsInGruppe = this.auswertbareTests(this.testsInGruppe);
        const ergebnisseInGruppe = testsInGruppe
            .flatMap(t => t.ergebnisse)

        interface IItemErgebnisse {
            item_id: number;
            testheft_id: number;
            anzahlItemsInAufgabe: number;
            ergebnisse: (IErgebnisseDB & { testheft: ITestheftAPI })[];
            loesungshaeufigkeiten: ILoesungshaeufigkeitenWerte;
        }

        let itemErgebisse: IItemErgebnisse[] = [];

        ergebnisseInGruppe.forEach(e => {
            const aufgabe = this.testheftliste
                .filter(testheft => testheft.testheft_id === e.testheft_id)[0]?.module
                .flatMap(modul => modul.aufgaben)
                .filter(eintrag => eintrag.items.some(item => item.item_id === e.item_id))[0] ?? null;

            if (!itemErgebisse.some(i => i.item_id === e.item_id && i.testheft_id === e.testheft_id))
                itemErgebisse.push({
                    item_id: e.item_id,
                    testheft_id: e.testheft_id,
                    anzahlItemsInAufgabe: aufgabe?.items.length ?? 1,
                    ergebnisse: [],
                    loesungshaeufigkeiten: {
                        anzahl: null,
                        r: null,
                        n: null,
                        f: null,
                        rProz: null,
                        nProz: null,
                        fProz: null
                    }
                });

            //region Testheft-Property nur auf das Testheft, das Modul, die Aufgabe, reduzieren, in denen dieses Item getestet wurde
            let ergebnisMitTestheft: IErgebnisseDB & { testheft: ITestheftAPI } = Utilities.cloneObject(e) as IErgebnisseDB & { testheft: ITestheftAPI };
            ergebnisMitTestheft.testheft = Utilities.cloneObject(this.testheftliste.filter(th => th.testheft_id === e.testheft_id)[0]);
            ergebnisMitTestheft.testheft.module = ergebnisMitTestheft.testheft.module
                .filter(m => m.aufgaben
                    .flatMap(a => a.items)
                    .flatMap(i => i.item_id).includes(e.item_id)
                );
            ergebnisMitTestheft.testheft.module[0].aufgaben = ergebnisMitTestheft.testheft.module[0].aufgaben
                .filter(a => a.items
                    .flatMap(i => i.item_id).includes(e.item_id)
                );
            ergebnisMitTestheft.testheft.module[0].aufgaben[0].items = ergebnisMitTestheft.testheft.module[0].aufgaben[0].items
                .filter(i => i.item_id === e.item_id
                );
            itemErgebisse.find(i => i.item_id === e.item_id && i.testheft_id === e.testheft_id).ergebnisse.push(ergebnisMitTestheft);
            //endregion

        });

        // Lösungshäufigkeiten für dieses Item berechnen
        itemErgebisse.forEach(i => {
            i.loesungshaeufigkeiten.anzahl = i.ergebnisse.length;
            i.loesungshaeufigkeiten.r = i.ergebnisse.filter(e => e.ergebnis === 1).length;
            i.loesungshaeufigkeiten.n = i.ergebnisse.filter(e => e.ergebnis === -1).length;
            i.loesungshaeufigkeiten.f = i.ergebnisse.filter(e => e.ergebnis === 0).length;

            let proz: number[] = [
                (i.loesungshaeufigkeiten.r / i.loesungshaeufigkeiten.anzahl) * 100,
                (i.loesungshaeufigkeiten.n / i.loesungshaeufigkeiten.anzahl) * 100,
                (i.loesungshaeufigkeiten.f / i.loesungshaeufigkeiten.anzahl) * 100
            ];
            let prozGerundet: number[] = Utilities.summenerhaltendRunden(proz);

            i.loesungshaeufigkeiten.rProz = prozGerundet[0];
            i.loesungshaeufigkeiten.nProz = prozGerundet[1];
            i.loesungshaeufigkeiten.fProz = prozGerundet[2];
        });
        //endregion

        // console.log(itemErgebisse);

        const chartData: { items: IFlussItem[] } = {items: []};

        let itemErgebisseOhneLhIqb: IItemErgebnisse[] = [];
        let itemErgebisseNzuGering: IItemErgebnisse[] = [];

        const printItemBezeichnung = (item: IItemErgebnisse): string => {
            const aufgabe = item.ergebnisse[0].testheft.module[0].aufgaben[0];
            if (this.sessionvariablen.projekt.weiche === 'vera5') {
                const testheft = this.testheftliste.find(eintrag => eintrag.testheft_id === item.ergebnisse[0].testheft.testheft_id) ?? null;
                const aufgabePosition = Utilities.getVera5Aufgabenposition(testheft, aufgabe.aufgabe_id, aufgabe.position_nr);
                return Utilities.printVera5Aufgabenposition(aufgabePosition, aufgabe.items[0].position_bezeichnung, item.anzahlItemsInAufgabe);
            }

            return `${(this.sessionvariablen.get['fach'] !== 'ma') ? this.domaeneEigenschaften(item.ergebnisse[0].testheft.module[0].domaene).kurzbezeichnung + ` ` : ``}${item.ergebnisse[0].testheft.module[0].typ.toUpperCase()} ${Utilities.printSchwierigkeit(item.ergebnisse[0].testheft.fach, item.ergebnisse[0].testheft.module[0].schwierigkeit)} ${aufgabe.position_nr.toString().padStart(2, ' ')}.${aufgabe.items[0].position_bezeichnung.toString().padEnd(2, ' ')}`;
        };

        const printTooltipBezeichnungen = (item: IItemErgebnisse): string => {
            const testheft = item.ergebnisse[0].testheft;
            const aufgabe = testheft.module[0].aufgaben[0];
            const aufgabeBezeichnung = (testheft.fach === 'ma') ? aufgabe.bezeichnung : `<span data-tooltip-aufgabe-id="${aufgabe.aufgabe_id}" data-tooltip-testheft-id="${testheft.testheft_id}">${aufgabe.bezeichnung}</span>`;
            return `<span data-tooltip-item-id="${item.item_id}" data-tooltip-testheft-id="${testheft.testheft_id}">${printItemBezeichnung(item)}</span> - ${aufgabeBezeichnung}`;
        };

        const verwendetGymnasialePilotierungswerte = this.sessionvariablen.projekt.weiche === 'vera8'
            ? this.sessionvariablen.schule.schulform === 'GY'
            : this.sessionvariablen.projekt.weiche === 'vera5' && this.schulstatus.testheft_schwierigkeit_mathematik === 2;

        itemErgebisse.forEach((i) => {

            // Pilotierungswert für Lösungshäufigkeit für diese Vergleichsgruppe bestimmen
            const item = i.ergebnisse[0].testheft.module[0].aufgaben[0].items[0];
            const iqb_lh = verwendetGymnasialePilotierungswerte ? item.iqb_lh_gym : item.iqb_lh_andere;

            if (iqb_lh !== null) {
                if (i.loesungshaeufigkeiten.anzahl >= RueckmeldungFluss.MINDESTANZAHL_BEARBEITUNGEN_PRO_ITEM) {
                    chartData.items.push({
                        item_id: i.item_id,
                        testheft_id: i.ergebnisse[0].testheft.testheft_id,
                        domaene: i.ergebnisse[0].testheft.module[0].domaene,
                        aufgabe_bezeichnung: i.ergebnisse[0].testheft.module[0].aufgaben[0].bezeichnung,
                        aufgabe_bezeichnung_kurz: printItemBezeichnung(i),
                        richtig: i.loesungshaeufigkeiten.rProz / 100,
                        iqb_lh: iqb_lh,
                    });
                } else {
                    itemErgebisseNzuGering.push(i);
                }
            } else {
                itemErgebisseOhneLhIqb.push(i);
            }
        });

        // Hinweistext zur angezeigten Gruppe anzeigen
        let vergleichsgruppenHinweis = '';
        if (this.sessionvariablen.projekt.weiche !== 'vera5' && this.sessionvariablen.schule.land !== 'ni') {
            vergleichsgruppenHinweis = verwendetGymnasialePilotierungswerte
                ? `<p>Ihre Schulform erhält Vergleichswerte aus der Pilotierungsstichprobe von Gymnasien.</p>`
                : `<p>Ihre Schulform erhält Vergleichswerte aus der Pilotierungsstichprobe von allen Schulformen außer Gymnasien. Berücksichtigen Sie,
            dass diese Daten nicht nur Ihre Schulform als Grundlage berücksichtigen und dadurch Verzerrungen im Vergleich zur Referenzkohorte auftreten können.</p>`;
        }
        const vergleiche = this.ermittleVergleiche(testsInGruppe)
            .filter(v => v.id === 'schule' || chartData.items.some(i => v.werte.has(`${i.testheft_id}-${i.item_id}`)));
        const vergleich = this.refreshVergleichsauswahl(vergleiche, chartData.items);
        (document.getElementById('informationen-zur-angezeigten-gruppe-oben') as HTMLDivElement).innerHTML = vergleichsgruppenHinweis;

        const hatNichtDargestellteItems = itemErgebisseOhneLhIqb.length > 0 || itemErgebisseNzuGering.length > 0;
        (document.getElementById('informationen-zur-angezeigten-gruppe-unten') as HTMLDivElement).innerHTML = (chartData.items.length > 0 && hatNichtDargestellteItems) ? `
        <aside class="hinweis icon">
        <h2>Nicht dargestellte Items</h2>
        ${(itemErgebisseOhneLhIqb.length > 0) ?
            `<p>Die folgenden Items werden aufgrund fehlender Pilotierungswerte nicht in der Grafik berücksichtigt:</p>
            <ul>
            ${itemErgebisseOhneLhIqb.map(i => {
                return `<li>${printTooltipBezeichnungen(i)}</li>`;
            }).join('\n')}
            </ul>
            ` : ``}
            
        ${(itemErgebisseNzuGering.length > 0) ?
            `<p>Die folgenden Items werden aufgrund einer zu geringen Anzahl an Bearbeitungen nicht in der Grafik berücksichtigt:</p>
            <ul>
            ${itemErgebisseNzuGering.map(i => {
                return `<li>${printTooltipBezeichnungen(i)}</li>`;
            }).join('\n')}
            </ul>
            ` : ``}
        </aside>
        ` : '';

        chartData.items.sort((a, b) => a.iqb_lh - b.iqb_lh);

        if (chartData.items.length === 0) {
            const flussContainer: HTMLDivElement = document.getElementById('lh-fluss-container') as HTMLDivElement;
            let hinweistext = `Für die Flussansicht müssen pro Item mindestens ${RueckmeldungFluss.MINDESTANZAHL_BEARBEITUNGEN_PRO_ITEM} auswertbare Bearbeitungen und ein Pilotierungswert vorliegen. Für kein Item sind beide Voraussetzungen erfüllt.`;

            if (itemErgebisse.length === 0)
                hinweistext = 'Für die ausgewählte Gruppe liegen keine auswertbaren Bearbeitungen von aktiven, anwesenden Schülern ohne F- oder S-Markierung vor.';
            else if (itemErgebisseNzuGering.length === itemErgebisse.length)
                hinweistext = `Für die Flussansicht müssen pro Item mindestens ${RueckmeldungFluss.MINDESTANZAHL_BEARBEITUNGEN_PRO_ITEM} auswertbare Bearbeitungen vorliegen. In der ausgewählten Gruppe ist diese Mindestanzahl bei keinem Item erreicht.`;
            else if (itemErgebisseOhneLhIqb.length === itemErgebisse.length)
                hinweistext = 'Für die bearbeiteten Items liegen keine Pilotierungswerte vor.';

            flussContainer.innerHTML = `
                <aside class="hinweis icon">
                    <h2>Keine Daten darstellbar</h2>
                    <p>${hinweistext}</p>
                </aside>
            `;
        } else if (this.sessionvariablen.get['fach'] !== 'ma')
            this.fillChartProDomaene(chartData, this.domaenenInGruppe.map(d => d.domaene), vergleich);
        else
            this.fillChartProDomaene(chartData, ['ma-gl'], vergleich);

        if (this.tresor?.offen === true)
            this.tresor.fillTresorLuecken();
    }

    /** Einheitliche Schülerbasis für eigene Gruppe, andere Lerngruppen und Schulwert */
    private auswertbareTests(tests: readonly ISchueler_testsAPI[]): ISchueler_testsAPI[] {
        const schuelerIds = this.auswertbareSchuelerIds();
        return tests.filter(t => t.nicht_anwesend === 0 && schuelerIds.has(t.schueler_id));
    }

    private auswertbareSchuelerIds(): ReadonlySet<number> {
        return new Set(this.schuelerliste
            .filter(s => s.is_deaktiviert === 0 && s.is_f !== 1 && s.is_s !== 1)
            .map(s => s.schueler_id));
    }

    /** Vergleichsgruppen müssen im gesamten Fach vollständig sein, nicht nur bei einzelnen Items */
    private ermittleVergleiche(testsInGruppe: readonly ISchueler_testsAPI[]): IFlussVergleich[] {
        const seite = document.getElementById('rueckmeldung-lh-fluss');
        const zeigeSchulwert = seite?.dataset.zeigeSchulwert === 'true';
        const zeigeAndereLerngruppen = seite?.dataset.zeigeAndereLerngruppen === 'true';
        if (!zeigeSchulwert && !zeigeAndereLerngruppen)
            return [];

        const testheftIds = new Set(testsInGruppe.map(t => t.testheft_id));
        const auswertbareSchuelerIds = this.auswertbareSchuelerIds();
        const vollstaendigeGruppen = this.gruppenliste
            .filter(g => g.typ === 'STAMMKLASSE' || g.kurs_fach === this.rmFilter.fach)
            .map(gruppe => {
                const schuelerIds = new Set(this.schuelerGruppenZuweisungen
                    .filter(z => z.gruppe_id === gruppe.gruppe_id && auswertbareSchuelerIds.has(z.schueler_id))
                    .map(z => z.schueler_id));
                const alleTests = this.testsInSchule.filter(t => schuelerIds.has(t.schueler_id));
                const hatAlleTests = Array.from(schuelerIds).every(id => alleTests.some(t => t.schueler_id === id));
                const vollstaendig = hatAlleTests && alleTests.every(t => t.nicht_anwesend === 1 || (t.loesungshaeufigkeiten !== null && t.ergebnisse.every(e => [-1, 0, 1].includes(e.ergebnis))));
                const tests = alleTests.filter(t => t.nicht_anwesend === 0);
                return {gruppe, tests, vollstaendig};
            })
            .filter(g => g.tests.length > 0 && g.vollstaendig);

        const vergleiche: IFlussVergleich[] = [];
        if (zeigeSchulwert) {
            // Klassen und Kurse können dieselben Tests enthalten
            const tests = Array.from(new Set(vollstaendigeGruppen.flatMap(g => g.tests)))
                .filter(t => testheftIds.has(t.testheft_id));
            const werte = this.aggregiereVergleichswerte(tests);
            const fehlenderVergleichsgrund = tests.length === 0
                ? 'Keine vollständige Lerngruppe mit auswertbaren Tests und demselben Testheft vorhanden.'
                : werte.size === 0
                    ? `Weniger als ${RueckmeldungFluss.MINDESTANZAHL_BEARBEITUNGEN_PRO_ITEM} auswertbare Bearbeitungen pro Item aus vollständigen Lerngruppen.`
                    : null;
            vergleiche.push({id: 'schule', bezeichnung: 'Schulwert', werte, fehlenderVergleichsgrund});
        }
        if (zeigeAndereLerngruppen) {
            vollstaendigeGruppen
                .filter(g => g.gruppe.gruppe_id !== this.gruppeAngezeigt.gruppe_id)
                .forEach(g => {
                    const tests = g.tests.filter(t => testheftIds.has(t.testheft_id));
                    vergleiche.push({id: `gruppe-${g.gruppe.gruppe_id}`, bezeichnung: g.gruppe.bezeichnung, werte: this.aggregiereVergleichswerte(tests), fehlenderVergleichsgrund: null});
                });
        }
        return vergleiche.filter(v => v.id === 'schule' || v.werte.size > 0);
    }

    /** Vergleichswerte werden erst nach ausdrücklicher Auswahl angezeigt */
    private refreshVergleichsauswahl(vergleiche: readonly IFlussVergleich[], items: readonly IFlussItem[]): IFlussVergleich | null {
        const filter = document.getElementById('fach-gruppe-filter');
        const filterZeile = filter?.querySelector<HTMLDivElement>('.filterauswahl') ?? null;
        if (filter === null || filterZeile === null)
            return null;

        const seite = document.getElementById('rueckmeldung-lh-fluss');
        const aktiviert = seite?.dataset.zeigeSchulwert === 'true' || seite?.dataset.zeigeAndereLerngruppen === 'true';
        let container = filter.querySelector<HTMLDivElement>('.lh-fluss-vergleichsauswahl');
        let hinweis = filter.querySelector<HTMLParagraphElement>('#lh-fluss-vergleichshinweis');
        if (!aktiviert) {
            this.vergleichsgruppeSelect?.destroy();
            this.vergleichsgruppeSelect = null;
            container?.remove();
            hinweis?.remove();
            return null;
        }

        if (container === null) {
            container = document.createElement('div');
            container.className = 'lh-fluss-vergleichsauswahl';
            container.innerHTML = `<label for="lh-fluss-vergleichsgruppe">Vergleichsgruppe:</label><div class="lh-fluss-vergleichsfeld"><select id="lh-fluss-vergleichsgruppe" class="lh-fluss-vergleichsgruppe"></select></div>`;
            if (filterZeile.style.display === 'none')
                filter.insertBefore(container, filter.querySelector('section.gruppeninformation'));
            else
                filterZeile.append(container);
        }

        if (hinweis === null) {
            hinweis = document.createElement('p');
            hinweis.id = 'lh-fluss-vergleichshinweis';
            hinweis.setAttribute('role', 'status');
            filter.append(hinweis);
        }

        const verfuegbareVergleiche = vergleiche.filter(v => items.some(i => v.werte.has(`${i.testheft_id}-${i.item_id}`)));
        const auswahlDeaktiviert = verfuegbareVergleiche.length === 0;
        if (!verfuegbareVergleiche.some(v => v.id === this.vergleichsgruppeId))
            this.vergleichsgruppeId = '-';

        try {
            if (this.vergleichsgruppeSelect === null) {
                this.vergleichsgruppeSelect = new TomSelect('#lh-fluss-vergleichsgruppe', {
                    create: false,
                    controlInput: null,
                    searchField: [],
                    onChange: (value: string): void => {
                        this.vergleichsgruppeId = value || '-';
                        this.refreshLhFlussansicht();
                    }
                });
            }

            const select = this.vergleichsgruppeSelect;
            select.clear(true);
            select.clearOptions();
            const optionen = verfuegbareVergleiche.map(v => ({value: v.id, text: v.bezeichnung}));
            select.addOption([{value: '-', text: auswahlDeaktiviert ? 'Nicht verfügbar' : '-'}, ...optionen]);
            select.setValue(this.vergleichsgruppeId, true);
            select.refreshOptions(false);
            if (auswahlDeaktiviert)
                select.disable();
            else
                select.enable();
            select.control.setAttribute('aria-disabled', String(auswahlDeaktiviert));
        } catch (error) {
            Utilities.errorToSentry(error);
        }

        const schulwert = vergleiche.find(v => v.id === 'schule') ?? null;
        const schulwertNichtVerfuegbar = schulwert !== null && !verfuegbareVergleiche.includes(schulwert);
        const grund = schulwert?.fehlenderVergleichsgrund ?? (items.length === 0
            ? 'Für die gewählte Lerngruppe sind keine Items darstellbar.'
            : 'Für die dargestellten Items liegen keine Vergleichswerte mit mindestens drei auswertbaren Bearbeitungen vor.');
        const hinweistext = `Schulwert derzeit nicht verfügbar: ${grund}`;
        hinweis.hidden = auswahlDeaktiviert || !schulwertNichtVerfuegbar;
        hinweis.textContent = hinweis.hidden ? '' : hinweistext;
        const vergleichsfeld = container.querySelector<HTMLDivElement>('.lh-fluss-vergleichsfeld');
        if (vergleichsfeld !== null) {
            if (auswahlDeaktiviert) {
                vergleichsfeld.dataset.tooltip = Tooltip.content`<p><strong>Keine Vergleichswerte verfügbar</strong></p><p>${grund}</p>`;
            } else {
                delete vergleichsfeld.dataset.tooltip;
            }
        }
        return verfuegbareVergleiche.find(v => v.id === this.vergleichsgruppeId) ?? null;
    }

    private bereinigeCharts(): void {
        this.chartBereinigungen.forEach(bereinigen => bereinigen());
        this.chartBereinigungen = [];
    }

    private aggregiereVergleichswerte(tests: readonly ISchueler_testsAPI[]): ReadonlyMap<string, number> {
        const ergebnisseProItem = new Map<string, IErgebnisseDB[]>();
        tests.forEach(t => t.ergebnisse.forEach(e => {
            const key = `${t.testheft_id}-${e.item_id}`;
            const ergebnisse = ergebnisseProItem.get(key) ?? [];
            ergebnisse.push(e);
            ergebnisseProItem.set(key, ergebnisse);
        }));

        const werte = new Map<string, number>();
        ergebnisseProItem.forEach((ergebnisse, key) => {
            if (ergebnisse.length < RueckmeldungFluss.MINDESTANZAHL_BEARBEITUNGEN_PRO_ITEM)
                return;

            const proz = [1, -1, 0].map(wert => ergebnisse.filter(e => e.ergebnis === wert).length / ergebnisse.length * 100);
            werte.set(key, Utilities.summenerhaltendRunden(proz)[0]);
        });
        return werte;
    }

    private fillChartProDomaene(chartData: { items: IFlussItem[] }, domaenen: string[], vergleich: IFlussVergleich | null): void {
        domaenen = domaenen.filter(d => chartData.items.some(i => i.domaene === d || d === 'ma-gl'));
        const flussContainer: HTMLDivElement = document.getElementById(`lh-fluss-container`) as HTMLDivElement;
        const portalStyles = getComputedStyle(document.documentElement);
        const primaryColor = portalStyles.getPropertyValue('--primary-color').trim() || '#283c8f';
        const primaryColorDark = portalStyles.getPropertyValue('--primary-color-dark-20').trim() || primaryColor;
        const accentColor = portalStyles.getPropertyValue('--accent-color').trim() || '#2fa0e0';
        this.refreshDruckkopf(vergleich);
        const schwierigkeitsTooltip = (this.sessionvariablen.projekt.weiche === 'vera5') ? Tooltip.html`
            <p><strong>So lesen Sie die Flussansicht</strong></p>
            <p>Die Aufgaben sind von oben nach unten anhand ihrer Lösungshäufigkeit im Landesschnitt der jeweiligen Testheftvariante von leicht nach schwierig sortiert. Die Raute zeigt, wie häufig die angezeigte Gruppe das jeweilige Item richtig gelöst hat.</p>
            <p>Der blaue Fluss bildet für jedes Item den erwartbaren Bereich der Lösungshäufigkeit ab. Liegt die Raute im rötlichen Bereich links vom Fluss, wurde das Item in der Gruppe seltener gelöst als im Landesschnitt zu erwarten. Im grünlichen Bereich rechts vom Fluss wurde es häufiger gelöst als erwartet.</p>
            <p>Bei kleinen Testgruppen können bereits einzelne Antworten deutliche prozentuale Ausschläge verursachen. Berücksichtigen Sie deshalb bei der Interpretation immer die Gruppengröße und den Unterrichtskontext.</p>
        ` : Tooltip.html`
            <p><strong>So lesen Sie die Flussansicht</strong></p>
            <p>Die Aufgaben sind von oben nach unten anhand der in der Pilotierung ermittelten Lösungswahrscheinlichkeit von leicht nach schwierig sortiert. Die Raute zeigt, wie häufig die angezeigte Gruppe das jeweilige Item richtig gelöst hat.</p>
            <p>Der blaue Fluss bildet für jedes Item den erwartbaren Bereich der Lösungshäufigkeit ab. Liegt die Raute im rötlichen Bereich links vom Fluss, wurde das Item in der Gruppe seltener gelöst als anhand des Pilotierungswerts zu erwarten. Im grünlichen Bereich rechts vom Fluss wurde es häufiger gelöst als erwartet.</p>
            <p>Bei kleinen Testgruppen können bereits einzelne Antworten deutliche prozentuale Ausschläge verursachen. Berücksichtigen Sie deshalb bei der Interpretation immer die Gruppengröße und den Unterrichtskontext.</p>
        `;

        flussContainer.innerHTML = `
            <div class="lh-fluss-domaenen">
                ${domaenen.map(domaene => `
                <div class="lh-fluss-domaene-gruppe">
                    ${(this.sessionvariablen.get['fach'] !== 'ma') ? `<h2 class="lh-fluss-domaene">Domäne: ${this.domaeneEigenschaften(domaene).bezeichnung}</h2>` : ``}
                    <div class="lh-fluss">
                        <div class="lh-fluss-itemliste" id="lh-fluss-itemliste-${domaene}" role="list"></div>
                        <div class="lh-fluss-chartbereich">
                            <div class="lh-fluss-chart" id="lh-fluss-chart-${domaene}"></div>
                            <div class="lh-fluss-zeilenhervorhebung" hidden></div>
                            <div class="lh-fluss-schwierigkeit" role="img" aria-label="Aufgabenschwierigkeit: von leicht nach schwer">
                                <div class="lh-fluss-schwierigkeit-pfeil" data-tooltip="${schwierigkeitsTooltip}">
                                    <svg class="lh-fluss-schwierigkeit-form" viewBox="0 0 34 320" preserveAspectRatio="none" aria-hidden="true">
                                        <path d="M 5.5 .5 H 28.5 V 303.5 H 33 L 17 319 L 1 303.5 H 5.5 Z" vector-effect="non-scaling-stroke"/>
                                    </svg>
                                    <span class="lh-fluss-schwierigkeit-bezeichnung"><strong>leicht</strong><span>Aufgabenschwierigkeit</span><strong>schwer</strong></span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="lh-fluss-legende" role="group" aria-label="Legende der Flussansicht">
                        <span class="lh-fluss-legende-eintrag">
                            <span class="lh-fluss-legende-raute" aria-hidden="true"></span>
                            <span>Ihre Lerngruppe</span>
                        </span>
                        <span class="lh-fluss-legende-eintrag">
                            <span class="lh-fluss-legende-korridor" aria-hidden="true"></span>
                            <span>${(this.sessionvariablen.projekt.weiche === 'vera5') ? 'Landesschnitt' : 'erwartete Lösungshäufigkeit'} <span class="lh-fluss-legende-zusatz">(±10 Prozentpunkte)</span></span>
                        </span>
                    </div>
                </div>
                `).join('')}
            </div>
        `;

        domaenen.forEach(domaene => {
            const chartDom: HTMLDivElement = document.getElementById(`lh-fluss-chart-${domaene}`) as HTMLDivElement;
            const itemlisteDom: HTMLDivElement = document.getElementById(`lh-fluss-itemliste-${domaene}`) as HTMLDivElement;
            const flussDom = chartDom.closest('div.lh-fluss') as HTMLDivElement;
            const zeilenhervorhebungDom = flussDom.querySelector('div.lh-fluss-zeilenhervorhebung') as HTMLDivElement;

            const items = chartData.items
                .filter(i => (i.domaene === domaene || domaene === 'ma-gl'));
            const count = items.length;
            const vergleicheInDomaene = vergleich !== null && items.some(i => vergleich.werte.has(`${i.testheft_id}-${i.item_id}`)) ? [vergleich] : [];
            const legendeDom = chartDom.closest('div.lh-fluss-domaene-gruppe').querySelector('div.lh-fluss-legende');
            vergleicheInDomaene.forEach(v => {
                const eintrag = document.createElement('span');
                eintrag.className = 'lh-fluss-legende-eintrag';
                const symbol = document.createElement('span');
                symbol.className = 'lh-fluss-legende-kreis';
                symbol.setAttribute('aria-hidden', 'true');
                const bezeichnung = document.createElement('span');
                bezeichnung.textContent = `Vergleich: ${v.bezeichnung}`;
                eintrag.append(symbol, bezeichnung);
                legendeDom.append(eintrag);
            });
            const rastergroesse = 34;
            const raster = {
                left: 12,
                right: 60,
                bottom: 18,
                top: 42
            };
            const xAchsenIntervalle = 10;
            const diagrammbreite = xAchsenIntervalle * rastergroesse;
            const chartBreite = raster.left + raster.right + diagrammbreite;
            const chartHoehe = Math.max(360, (count + 1) * rastergroesse + raster.top + raster.bottom);

            // Randwerte führen die Flächen über die leeren Randzeilen bis zur Achsengrenze fort
            const flaechenWerteMitRand = (werte: readonly number[]): [number, number][] =>
                [werte[0], ...werte, werte[werte.length - 1]].map((wert, index) => [wert, index]);

            chartDom.style.width = `${chartBreite}px`;
            chartDom.style.height = `${chartHoehe}px`;
            itemlisteDom.style.height = `${chartHoehe}px`;

            const itemzeilen = items.map((item, dataIndex) => {
                const zeile = document.createElement('div');
                zeile.className = 'lh-fluss-item';
                zeile.dataset.dataIndex = dataIndex.toString();
                zeile.tabIndex = 0;
                zeile.title = `${item.aufgabe_bezeichnung_kurz} - ${item.aufgabe_bezeichnung}`;
                const vergleichsangaben = vergleicheInDomaene.flatMap(v => {
                    const wert = v.werte.get(`${item.testheft_id}-${item.item_id}`) ?? null;
                    return wert === null ? [] : [`${v.bezeichnung}: ${wert}%`];
                });
                zeile.title += `\nIhre Lerngruppe: ${Math.round(item.richtig * 100)}%${vergleichsangaben.length > 0 ? `\n${vergleichsangaben.join('\n')}` : ''}`;
                zeile.setAttribute('role', 'listitem');

                const position = document.createElement('span');
                position.className = 'lh-fluss-item-position';
                position.textContent = item.aufgabe_bezeichnung_kurz;
                position.dataset.tooltipItemId = item.item_id.toString();
                position.dataset.tooltipTestheftId = item.testheft_id.toString();

                const bezeichnung = document.createElement('span');
                bezeichnung.className = 'lh-fluss-item-bezeichnung';
                bezeichnung.textContent = item.aufgabe_bezeichnung;

                zeile.append(position, bezeichnung);
                itemlisteDom.append(zeile);
                return zeile;
            });

            const aktualisiereBeschriftungsbreite = (): void => {
                const beschriftungsbreite = Math.ceil(Math.max(...itemzeilen.flatMap(zeile =>
                    Array.from(zeile.children).map((element: Element) => {
                        const textbereich = document.createRange();
                        textbereich.selectNodeContents(element);
                        return textbereich.getBoundingClientRect().width;
                    })
                ))) + 1;
                itemlisteDom.style.width = `${beschriftungsbreite}px`;
            };

            aktualisiereBeschriftungsbreite();
            void document.fonts.ready.then(aktualisiereBeschriftungsbreite);
            flussDom.style.setProperty('--lh-fluss-rastergroesse', `${rastergroesse}px`);
            flussDom.style.setProperty('--lh-fluss-diagrammbreite', `${diagrammbreite}px`);
            flussDom.style.setProperty('--lh-fluss-raster-oben', `${raster.top}px`);
            flussDom.style.setProperty('--lh-fluss-raster-unten', `${raster.bottom}px`);

            const myChart = echarts.init(chartDom, '', {renderer: 'svg'});

            // Chart configuration
            const option: any = {
                animation: false,
                textStyle: {
                    color: '#34413f',
                    fontFamily: 'Inter, system-ui, sans-serif'
                },
                grid: {
                    ...raster,
                    containLabel: false
                },
                yAxis: {
                    type: 'category',
                    position: 'left',
                    boundaryGap: false,
                    min: 0,
                    max: count + 1,
                    data: ['rand-unten', ...items.map(i => `${i.testheft_id}-${i.item_id}`), 'rand-oben'],
                    axisLabel: {
                        show: false
                    },
                    axisLine: {
                        show: true,
                        lineStyle: {
                            color: '#aeb9b7',
                            width: 1
                        }
                    },
                    axisTick: {
                        show: true,
                        alignWithLabel: true,
                        interval: (index: number): boolean => index > 0 && index <= count,
                        inside: false,
                        length: 5,
                        lineStyle: {
                            color: '#7b8583',
                            width: 1
                        }
                    },
                    splitLine: {
                        show: true,
                        alignWithLabel: true,
                        lineStyle: {
                            color: '#e3e9e7',
                            width: 1,
                            type: 'solid'
                        }
                    }
                },
                xAxis: {
                    type: 'value',
                    min: 0,
                    max: 100,
                    interval: 10,
                    position: 'top',
                    axisLabel: {
                        formatter: '{value}%',
                        color: '#5d6967',
                        fontFamily: 'Inter, system-ui, sans-serif',
                        fontSize: 12,
                        margin: 8
                    },
                    axisLine: {
                        show: true,
                        lineStyle: {
                            color: '#aeb9b7'
                        }
                    },
                    axisTick: {
                        show: true,
                        inside: false,
                        lineStyle: {
                            color: '#aeb9b7'
                        }
                    },
                    splitLine: {
                        show: true,
                        lineStyle: {
                            color: '#d9e1df',
                            width: 1,
                            type: 'solid'
                        }
                    }
                },
                series: [
                    {
                        name: this.gruppeAngezeigt.bezeichnung,
                        type: 'line',
                        symbol: 'diamond',
                        symbolSize: 12,
                        lineStyle: {
                            width: 0
                        },
                        itemStyle: {
                            color: primaryColor,
                            borderColor: primaryColor,
                            borderWidth: 1
                        },
                        emphasis: {
                            itemStyle: {
                                color: accentColor,
                                borderColor: primaryColorDark,
                                borderWidth: 1
                            },
                            symbolSize: 17
                        },
                        silent: true,
                        data: items.map((i, index) => [i.richtig * 100, index + 1]),
                        z: 10
                    },
                    ...vergleicheInDomaene.map(v => ({
                        name: v.bezeichnung,
                        type: 'line',
                        symbol: 'circle',
                        symbolSize: 7,
                        lineStyle: {width: 0},
                        itemStyle: {color: primaryColorDark, borderColor: '#fff', borderWidth: 1},
                        silent: true,
                        data: items.map((i, index) => [v.werte.get(`${i.testheft_id}-${i.item_id}`) ?? null, index + 1]),
                        z: 6
                    })),
                    {
                        name: 'unter',
                        type: 'line',
                        stack: 'Total',
                        symbol: 'none',
                        smooth: true,
                        areaStyle: {
                            opacity: 0.06,
                            color: '#750707'
                        },
                        lineStyle: {
                            width: 0,
                            color: '#750707'
                        },
                        silent: true,
                        tooltip: {show: false},
                        data: flaechenWerteMitRand(items.map(i => Math.max((i.iqb_lh * 100) - 10, 0))),
                        z: 0
                    },
                    {
                        name: 'in',
                        type: 'line',
                        stack: 'Total',
                        symbol: 'none',
                        smooth: true,
                        areaStyle: {
                            opacity: 0.5,
                            color: '#2fa0e0'
                        },
                        lineStyle: {
                            width: 0,
                            color: '#2fa0e0'
                        },
                        silent: true,
                        tooltip: {show: false},
                        data: flaechenWerteMitRand(items.map(i => Math.min(20, 20 - 10 + (i.iqb_lh * 100)))),
                        z: 0
                    },
                    {
                        name: 'über',
                        type: 'line',
                        stack: 'Total',
                        symbol: 'none',
                        smooth: true,
                        areaStyle: {
                            opacity: 0.05,
                            color: '#3bb725'
                        },
                        lineStyle: {
                            width: 0,
                            color: '#3bb725'
                        },
                        silent: true,
                        tooltip: {show: false},
                        data: flaechenWerteMitRand(items.map(() => 100)),
                        z: 0
                    }
                ]
            };

            // Set chart options and render
            myChart.setOption(option);

            let aktiveZeile: number | null = null;
            let hoverZeile: number | null = null;
            let fixierteZeile: number | null = null;
            let zeilenpositionen: number[] = [];

            const aktualisiereZeilenpositionen = (): void => {
                zeilenpositionen = items.map((item, dataIndex) => {
                    const position = Number(myChart.convertToPixel({yAxisIndex: 0}, `${item.testheft_id}-${item.item_id}`));
                    itemzeilen[dataIndex].style.top = `${position}px`;
                    return position;
                });
            };

            const zeigeAktiveZeile = (dataIndex: number | null): void => {
                if (aktiveZeile === dataIndex) return;

                if (aktiveZeile !== null) {
                    itemzeilen[aktiveZeile].classList.remove('aktiv');
                    myChart.dispatchAction({type: 'downplay', seriesIndex: 0, dataIndex: aktiveZeile});
                }

                aktiveZeile = dataIndex;
                if (dataIndex === null) {
                    zeilenhervorhebungDom.hidden = true;
                    return;
                }

                itemzeilen[dataIndex].classList.add('aktiv');
                zeilenhervorhebungDom.style.top = `${zeilenpositionen[dataIndex] - rastergroesse / 2}px`;
                zeilenhervorhebungDom.hidden = false;
                myChart.dispatchAction({type: 'highlight', seriesIndex: 0, dataIndex: dataIndex});
            };

            const aktualisiereAktiveZeile = (): void => {
                zeigeAktiveZeile(fixierteZeile ?? hoverZeile);
            };

            const ermittleNaechsteZeile = (yPosition: number): number => {
                return zeilenpositionen.reduce((naechsterIndex, position, index) =>
                    Math.abs(position - yPosition) < Math.abs(zeilenpositionen[naechsterIndex] - yPosition) ? index : naechsterIndex
                , 0);
            };

            const aktiviereNaechsteZeile = (event: PointerEvent): void => {
                const yPosition = event.clientY - chartDom.getBoundingClientRect().top;
                const dataIndex = ermittleNaechsteZeile(yPosition);
                hoverZeile = Math.abs(zeilenpositionen[dataIndex] - yPosition) <= rastergroesse / 2 ? dataIndex : null;
                aktualisiereAktiveZeile();
            };

            aktualisiereZeilenpositionen();
            chartDom.addEventListener('pointermove', aktiviereNaechsteZeile);
            itemlisteDom.addEventListener('pointermove', aktiviereNaechsteZeile);
            flussDom.addEventListener('pointerleave', () => {
                hoverZeile = null;
                aktualisiereAktiveZeile();
            });
            const tooltipTriggers = itemzeilen.map(zeile => zeile.querySelector('span[data-tooltip-item-id]') as HTMLElement);
            chartDom.addEventListener('click', event => {
                const chartPosition = chartDom.getBoundingClientRect();
                const xPosition = event.clientX - chartPosition.left;
                const yPosition = event.clientY - chartPosition.top;
                if (xPosition < raster.left || xPosition > raster.left + diagrammbreite)
                    return;

                const dataIndex = ermittleNaechsteZeile(yPosition);
                if (Math.abs(zeilenpositionen[dataIndex] - yPosition) > rastergroesse / 2)
                    return;

                event.stopPropagation();
                tooltipTriggers[dataIndex].click();
            });
            const tooltipObservers: MutationObserver[] = [];
            itemzeilen.forEach((zeile, dataIndex) => {
                const tooltipTrigger = tooltipTriggers[dataIndex];
                const tooltipObserver = new MutationObserver(() => {
                    if (tooltipTrigger.getAttribute('aria-expanded') === 'true')
                        fixierteZeile = dataIndex;
                    else if (fixierteZeile === dataIndex)
                        fixierteZeile = null;
                    aktualisiereAktiveZeile();
                });
                tooltipObserver.observe(tooltipTrigger, {attributes: true, attributeFilter: ['aria-expanded']});
                tooltipObservers.push(tooltipObserver);

                zeile.addEventListener('click', event => {
                    if (event.target === tooltipTrigger)
                        return;

                    event.stopPropagation();
                    tooltipTrigger.click();
                });
                zeile.addEventListener('focus', () => {
                    hoverZeile = dataIndex;
                    aktualisiereAktiveZeile();
                });
                zeile.addEventListener('blur', () => {
                    hoverZeile = null;
                    aktualisiereAktiveZeile();
                });
                zeile.addEventListener('keydown', event => {
                    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;

                    event.preventDefault();
                    const naechsterIndex = Math.min(Math.max(dataIndex + (event.key === 'ArrowUp' ? 1 : -1), 0), itemzeilen.length - 1);
                    itemzeilen[naechsterIndex].focus();
                });
            });

            const aktualisiereChartgroesse = (): void => {
                myChart.resize();
                aktualisiereZeilenpositionen();
            };
            window.addEventListener('resize', aktualisiereChartgroesse);
            this.chartBereinigungen.push(() => {
                window.removeEventListener('resize', aktualisiereChartgroesse);
                tooltipObservers.forEach(observer => observer.disconnect());
                myChart.dispose();
            });

        });
    }

    /** Aktualisiert die fachlichen Angaben und die Legende des Ausdrucks */
    private refreshDruckkopf(vergleich: IFlussVergleich | null = null): void {
        const druckFach = document.querySelector<HTMLElement>(`#rueckmeldung-druckfach`);
        const druckGruppe = document.querySelector<HTMLElement>(`#rueckmeldung-druckgruppe`);
        const druckLegende = document.querySelector<HTMLElement>(`#rueckmeldung-drucklegende`);
        const fach = this.rmFilter?.fach ?? this.sessionvariablen.get['fach'] ?? null;

        if (druckFach !== null)
            druckFach.textContent = (fach === null || fach === 'alle') ? '' : Utilities.printFachLang(fach);
        if (druckGruppe !== null)
            druckGruppe.textContent = this.gruppeAngezeigt?.bezeichnung ?? '';
        if (druckLegende !== null) {
            druckLegende.textContent = (this.sessionvariablen.projekt.weiche === 'vera5')
                ? 'Raute: Anteil richtig gelöster Items in der Gruppe · blauer Korridor: erwartbarer Bereich von ±10 Prozentpunkten um den Landesschnitt der Testheftvariante.'
                : 'Raute: Anteil richtig gelöster Items in der Gruppe · blauer Korridor: erwartbarer Bereich von ±10 Prozentpunkten um den Pilotierungswert.';
            if (vergleich?.id === 'schule')
                druckLegende.textContent += ' Kreis: Schulwert aus vollständigen Lerngruppen mit demselben Testheft, einschließlich der eigenen vollständigen Gruppe, nach Schülerzahl gewichtet.';
            else if (vergleich !== null)
                druckLegende.textContent += ` Kreis: Vergleich mit der vollständigen Lerngruppe ${vergleich.bezeichnung} mit demselben Testheft.`;
            if (vergleich !== null && vergleich.werte.size === 0)
                druckLegende.textContent += ' Für diesen Vergleich sind derzeit keine Werte verfügbar.';
            druckLegende.textContent += ' Alle Gruppen- und Schulwerte ohne F- und S-Schüler.';
        }
    }

}
